
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FeatureGuide } from '@/types/knowledgeBase';
import ReactMarkdown from 'react-markdown';
import { ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GuideDetailProps {
  selectedGuide: FeatureGuide | null;
  allGuides: FeatureGuide[];
  onSelectGuide: (guide: FeatureGuide) => void;
}

const GuideDetail: React.FC<GuideDetailProps> = ({ 
  selectedGuide, 
  allGuides, 
  onSelectGuide 
}) => {
  if (!selectedGuide) {
    return (
      <Card className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <h3 className="text-lg font-medium">No guide selected</h3>
          <p className="text-gray-500">Select a guide from the sidebar to view its contents</p>
        </div>
      </Card>
    );
  }

  // Find related guides by category
  const relatedGuides = allGuides
    .filter(guide => 
      guide.id !== selectedGuide.id && 
      guide.category === selectedGuide.category
    )
    .slice(0, 3);

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">{selectedGuide.title}</CardTitle>
              <CardDescription className="flex items-center mt-2">
                <Clock className="h-4 w-4 mr-1" />
                Last updated: {selectedGuide.lastUpdated}
                {selectedGuide.isNew && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    New
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="prose max-w-none">
            {selectedGuide.content && (
              <ReactMarkdown>{selectedGuide.content}</ReactMarkdown>
            )}
          </div>
        </CardContent>
      </Card>

      {relatedGuides.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Related Guides</CardTitle>
            <CardDescription>Other guides in the {selectedGuide.category} category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {relatedGuides.map(guide => (
                <Button 
                  key={guide.id}
                  variant="ghost" 
                  className="w-full justify-between text-left"
                  onClick={() => onSelectGuide(guide)}
                >
                  <span>{guide.title}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default GuideDetail;
